import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import Modal from '@components/shared/Modal/Modal';
import Button from '@components/shared/Button/Button';
import styles from './Services.module.css';

const Services: React.FC = () => {
  const { config } = useConfig();
  const [selectedService, setSelectedService] = useState<number | null>(null);

  if (!config || !config.components.services.enabled) return null;

  const { services } = config.components;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const handleServiceClick = (index: number) => {
    if (services.items[index].detailedDescription || services.items[index].features) {
      setSelectedService(index);
    }
  };

  const closeModal = () => {
    setSelectedService(null);
  };

  return (
    <section id="services" className={styles.services}>
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <motion.div className={styles.header} variants={itemVariants}>
            <h2 className={styles.title}>Our Services</h2>
            <div className={styles.divider}></div>
            <p className={styles.subtitle}>
              Professional solutions tailored to meet your specific needs
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className={styles.grid}>
            {services.items.map((service, index) => (
              <motion.div
                key={index}
                className={styles.serviceCard}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                onClick={() => handleServiceClick(index)}
              >
                {service.image ? (
                  <div className={styles.serviceImage}>
                    <img src={service.image} alt={service.title} />
                    <div className={styles.imageOverlay}>
                      <i className={`${service.icon} ${styles.overlayIcon}`}></i>
                    </div>
                  </div>
                ) : (
                  <div className={styles.serviceIcon}>
                    <i className={service.icon}></i>
                  </div>
                )}

                <div className={styles.serviceContent}>
                  <h3 className={styles.serviceTitle}>{service.title}</h3>
                  <p className={styles.serviceDescription}>{service.description}</p>

                  {service.price && (
                    <p className={styles.servicePrice}>{service.price}</p>
                  )}

                  {(service.detailedDescription || service.features) && (
                    <button className={styles.learnMoreButton}>
                      Learn More
                      <i className="fas fa-arrow-right" style={{ marginLeft: '0.5rem' }}></i>
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Service Detail Modal */}
      {selectedService !== null && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={services.items[selectedService].title}
          size="lg"
        >
          <div className={styles.modalContent}>
            {services.items[selectedService].image && (
              <img
                src={services.items[selectedService].image}
                alt={services.items[selectedService].title}
                className={styles.modalImage}
              />
            )}

            <div className={styles.modalBody}>
              {services.items[selectedService].detailedDescription && (
                <p className={styles.detailedDescription}>
                  {services.items[selectedService].detailedDescription}
                </p>
              )}

              {services.items[selectedService].features &&
                services.items[selectedService].features!.length > 0 && (
                  <div className={styles.features}>
                    <h4 className={styles.featuresTitle}>Features Include:</h4>
                    <ul className={styles.featuresList}>
                      {services.items[selectedService].features!.map((feature, idx) => (
                        <li key={idx} className={styles.featureItem}>
                          <i className="fas fa-check-circle" style={{ marginRight: '0.5rem', color: 'var(--color-primary)' }}></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {services.items[selectedService].price && (
                <div className={styles.modalPrice}>
                  <strong>Price:</strong> {services.items[selectedService].price}
                </div>
              )}

              <div className={styles.modalActions}>
                <Button variant="primary" size="lg" onClick={() => window.location.href = '#contact'}>
                  Get a Quote
                </Button>
                <Button variant="outline" size="lg" onClick={closeModal}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default Services;